import { useEffect, useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '../ui/button';

export const REVIEW_BANNER_DISMISSED_KEY = 'owaza_review_banner_dismissed';

// SSR-safe read of the dismissal flag from localStorage.
const readDismissed = () => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(REVIEW_BANNER_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Dismissible "leave us a Google review" banner shown at the top of the
 * member portal dashboard. Warm, non-intrusive card that links members to the
 * existing /review funnel (opens in a new tab so they keep their portal
 * session). Dismissal is persisted in localStorage so it does not reappear.
 */
const ReviewBanner = () => {
  const [dismissed, setDismissed] = useState(readDismissed);

  // Re-check on mount in case initial state was computed during SSR.
  useEffect(() => {
    if (readDismissed()) setDismissed(true);
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(REVIEW_BANNER_DISMISSED_KEY, 'true');
      } catch {
        /* ignore write failures (private mode, quota) */
      }
    }
  };

  if (dismissed) return null;

  return (
    <div
      data-testid="review-banner"
      role="region"
      aria-label="Leave a Google review"
      className="member-card mb-6 overflow-hidden border-l-4 border-l-[#9B1B30] bg-gradient-to-r from-[#9B1B30]/5 to-amber-50/40 dark:from-[#9B1B30]/15 dark:to-amber-900/10"
    >
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
        <p className="flex-1 text-sm text-stone-700 dark:text-stone-200 font-dm-sans">
          ⭐ Love training at Owaza? Help another family find us — leave a quick Google review. It takes 60 seconds.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            asChild
            variant="memberPrimary"
            size="sm"
            className="w-full sm:w-auto"
          >
            <a href="/review" target="_blank" rel="noopener noreferrer">
              Leave a review →
            </a>
          </Button>
          <button
            type="button"
            aria-label="Dismiss review reminder"
            onClick={handleDismiss}
            className="rounded-full p-1.5 text-stone-500 hover:bg-stone-200/70 hover:text-stone-700 dark:text-stone-400 dark:hover:bg-stone-700 dark:hover:text-stone-200 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewBanner;
